/* eslint-disable max-lines */
import React, { useState, useEffect, useContext } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form } from 'reactstrap';
import { toast } from 'react-toastify';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import { UserWithEmployee } from './UserSelectionModal';
import PerformanceImprovementService from '../../../../services/PerformanceImprovement';
import PriorDiscussionsTable from './PriorDiscussionsTable';
import { useImprovementForm } from './Improvement/Form/useImprovementForm';
import { INITIAL_FORM_DATA } from './Improvement/Form/formConstants';
import {
  UserInfoSection,
  PolicySection,
  ReasonForPerformanceImprovementPlanSection,
  ExpectationsAndGoalsSection,
  TimelineAndDeadlineSection,
  ResourcesAndSupportSection,
  PrivateNotesSection,
} from './Improvement/Form/FormSections';

interface ImprovementFormProps {
  isOpen: boolean;
  toggle: () => void;
  selectedUsers?: UserWithEmployee[];
  onSuccess?: () => void;
  editMode?: boolean;
  performanceImprovementId?: string;
}

interface FormData {
  name: string;
  date: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  reasonForPerformanceImprovementPlan: string;
  expectationsAndGoals: string;
  timelineAndDeadline: string;
  resourcesAndSupport: string;
  privateNotes: string;
}

const ImprovementForm = ({
  isOpen,
  toggle,
  selectedUsers = [],
  onSuccess,
  editMode = false,
  performanceImprovementId,
}: ImprovementFormProps) => {
  const { token, currentSchool }: any = useContext(AuthContext);
  const schoolId = currentSchool?.value;
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [submittedForms, setSubmittedForms] = useState<Map<string, FormData>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentUser = selectedUsers[currentUserIndex];
  const isLastUser = currentUserIndex === selectedUsers.length - 1;
  const hasMultipleUsers = !editMode && selectedUsers.length > 1;

  const { formData, errors, policyViolations, priorDiscussions, handleInputChange, validateForm, setFormData } =
    useImprovementForm(isOpen, token, currentUser, submittedForms, editMode, performanceImprovementId);

  useEffect(() => {
    if (!isOpen) {
      setCurrentUserIndex(0);
      setSubmittedForms(new Map());
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const saveCurrentForm = () => {
    if (currentUser) {
      const newSubmittedForms = new Map(submittedForms);
      newSubmittedForms.set(currentUser.id, { ...formData });
      setSubmittedForms(newSubmittedForms);
    }
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    saveCurrentForm();
    setCurrentUserIndex((prev: number) => prev + 1);
  };

  const handlePrevious = () => {
    saveCurrentForm();
    setCurrentUserIndex((prev: number) => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent, approved: boolean = true) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (editMode) {
      await submitEditForm(approved);
    } else {
      await submitDirectly(approved);
    }
  };

  const handleSaveDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (editMode) {
      await submitEditForm(false);
    } else {
      await submitDirectly(false);
    }
  };

  const submitDirectly = async (approved: boolean) => {
    if (!validateForm()) return;
    saveCurrentForm();

    setIsSubmitting(true);
    try {
      const service = PerformanceImprovementService(token);
      const finalForms = new Map(submittedForms);
      if (currentUser) {
        finalForms.set(currentUser.id, { ...formData });
      }

      const entries = Array.from(finalForms.entries());
      const performanceImprovements = entries.map(([userId, formData]: [string, FormData]) => ({
        userId,
        name: formData.name,
        date: formData.date,
        position: formData.position,
        department: formData.department,
        supervisor: formData.supervisor,
        dateOfHire: formData.dateOfHire,
        policyViolationIds: formData.policyViolationIds,
        reasonForPerformanceImprovementPlan: formData.reasonForPerformanceImprovementPlan,
        expectationsAndGoals: formData.expectationsAndGoals,
        timelineAndDeadline: formData.timelineAndDeadline,
        resourcesAndSupport: formData.resourcesAndSupport,
        privateNotes: formData.privateNotes,
        approved,
      }));

      await service.create({ schoolId, performanceImprovements });

      toast.success(
        approved
          ? `${
              performanceImprovements.length === 1
                ? 'Saved as pending for finalization'
                : `${performanceImprovements.length} Performance Improvements saved as pending for finalization`
            }. Download the document, get it signed, then finalize from Pending Finalization.`
          : `${
              performanceImprovements.length === 1 ? 'Draft saved' : `${performanceImprovements.length} drafts saved`
            } successfully!`,
      );

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error submitting performance improvements:', error);
      toast.error(error?.response?.data?.message || 'Error submitting performance improvements. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitEditForm = async (approved: boolean = true) => {
    if (!performanceImprovementId) return;

    setIsSubmitting(true);
    try {
      const service = PerformanceImprovementService(token);
      const updateData = {
        schoolId,
        date: formData.date,
        policyViolationIds: formData.policyViolationIds,
        reasonForPerformanceImprovementPlan: formData.reasonForPerformanceImprovementPlan,
        expectationsAndGoals: formData.expectationsAndGoals,
        timelineAndDeadline: formData.timelineAndDeadline,
        resourcesAndSupport: formData.resourcesAndSupport,
        privateNotes: formData.privateNotes,
        approved,
      };

      await service.update(performanceImprovementId, updateData);

      toast.success(approved ? 'Performance Improvement updated successfully!' : 'Draft saved successfully!');

      if (onSuccess) onSuccess();
      toggle();
    } catch (error: any) {
      console.error('Error updating performance improvement:', error);
      toast.error(error?.response?.data?.message || 'Error updating performance improvement. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setCurrentUserIndex(0);
    setSubmittedForms(new Map());
    setFormData(INITIAL_FORM_DATA);
    toggle();
  };

  if (selectedUsers.length === 0 && !editMode) {
    return (
      <Modal isOpen={isOpen} toggle={handleCancel} size="lg">
        <ModalHeader toggle={handleCancel}>Performance Improvement</ModalHeader>
        <ModalBody>
          <p className="text-muted">Please select at least one employee to create a Performance Improvement form.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCancel}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="xl" style={{ maxWidth: '1200px' }}>
      <ModalHeader toggle={handleCancel}>
        {editMode ? 'Edit Performance Improvement' : 'Performance Improvement'}
        {hasMultipleUsers && (
          <span className="ml-2 text-muted" style={{ fontSize: '0.9rem' }}>
            ({currentUserIndex + 1} of {selectedUsers.length})
          </span>
        )}
      </ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Form onSubmit={handleSubmit}>
          <UserInfoSection formData={formData} errors={errors} onChange={handleInputChange} />

          <PriorDiscussionsTable discussions={priorDiscussions} />

          <PolicySection
            formData={formData}
            errors={errors}
            policyViolations={policyViolations}
            onChange={handleInputChange}
          />

          <ReasonForPerformanceImprovementPlanSection
            value={formData.reasonForPerformanceImprovementPlan}
            error={errors.reasonForPerformanceImprovementPlan}
            onChange={(value) => handleInputChange('reasonForPerformanceImprovementPlan', value)}
          />

          <ExpectationsAndGoalsSection
            value={formData.expectationsAndGoals}
            error={errors.expectationsAndGoals}
            onChange={(value) => handleInputChange('expectationsAndGoals', value)}
          />

          <TimelineAndDeadlineSection
            value={formData.timelineAndDeadline}
            error={errors.timelineAndDeadline}
            onChange={(value) => handleInputChange('timelineAndDeadline', value)}
          />

          <ResourcesAndSupportSection
            value={formData.resourcesAndSupport}
            error={errors.resourcesAndSupport}
            onChange={(value) => handleInputChange('resourcesAndSupport', value)}
          />

          <PrivateNotesSection
            value={formData.privateNotes}
            onChange={(value) => handleInputChange('privateNotes', value)}
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <div className="ml-auto">
          {!editMode && hasMultipleUsers && currentUserIndex > 0 && (
            <Button color="outline-secondary" onClick={handlePrevious} className="mr-2">
              <i className="fas fa-arrow-left mr-2" />
              Previous
            </Button>
          )}
          {!editMode && hasMultipleUsers && !isLastUser ? (
            <Button color="primary" onClick={handleNext}>
              <i className="fas fa-arrow-right mr-2" />
              Next
            </Button>
          ) : (
            <>
              <Button color="warning" onClick={handleSaveDraft} disabled={isSubmitting} className="mr-2">
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2" />
                    Save Draft
                  </>
                )}
              </Button>
              <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-2" />
                    {hasMultipleUsers ? 'Review & Submit' : 'Submit'}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ImprovementForm;
